import type { AutomationParameter, ParameterValue, RunStrategy, TargetSelection } from '@/types/api';

/** Every parameter at its default: what a form starts from. */
export function initialValues(parameters: AutomationParameter[]): Record<string, ParameterValue> {
  return Object.fromEntries(parameters.map((parameter) => [parameter.key, parameter.default]));
}

/**
 * Only the values a person changed from their defaults.
 *
 * Sent instead of everything, so a run records what was chosen, and a default that improves later
 * reaches a retry of a run that never chose it.
 */
export function changedValues(
  parameters: AutomationParameter[],
  values: Record<string, ParameterValue>,
): Record<string, ParameterValue> {
  const changed: Record<string, ParameterValue> = {};

  for (const parameter of parameters) {
    const value = values[parameter.key];

    if (value === undefined) {
      continue;
    }

    if (JSON.stringify(value) !== JSON.stringify(parameter.default) || parameter.required) {
      changed[parameter.key] = value;
    }
  }

  return changed;
}

/** The API's field errors for one form, keyed without their `parameters.` prefix. */
export function parameterErrors(errors: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(errors)
      .filter(([key]) => key.startsWith('parameters.'))
      .map(([key, message]) => [key.slice('parameters.'.length), message]),
  );
}

/** One server first, five at a time after that, and stop at the first failure. */
export const SAFE_STRATEGY: RunStrategy = { canary: true, batch_size: 5, max_failures: 0 };

export const EMPTY_SELECTION: TargetSelection = { mode: 'servers', values: [] };

/**
 * The selection a launch page starts with.
 *
 * Opened from a server's own page (`?server=<id>`), that server is already chosen; otherwise nothing
 * is. Only ever one server, and only as a starting point: the picker shows it and it can be changed
 * like any other choice.
 */
export function initialSelection(params: URLSearchParams): TargetSelection {
  const server = params.get('server');

  return server !== null && /^[0-9a-f-]{36}$/i.test(server) ? { mode: 'servers', values: [server] } : EMPTY_SELECTION;
}

/**
 * The query string that carries a server chosen on its own page through a list page to a launch page,
 * or an empty string.
 */
export function serverQuery(params: URLSearchParams): string {
  const server = params.get('server');

  return server !== null && /^[0-9a-f-]{36}$/i.test(server) ? `?server=${server}` : '';
}

/** Whether a selection names something, so it is worth previewing and running. */
export function isCompleteSelection(selection: TargetSelection): boolean {
  return selection.mode === 'all' || selection.values.length > 0;
}

/** Splits a free-text list the way the API does: commas, spaces and new lines. */
export function splitList(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter((item) => item !== '');
}
